import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, withModifiers as _withModifiers, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, createTextVNode as _createTextVNode, mergeProps as _mergeProps, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "c-gen-nav__wrapper"
}
const _hoisted_2 = { class: "c-gen-nav__top" }
const _hoisted_3 = { class: "c-gen-nav__grid" }
const _hoisted_4 = { class: "c-gen-nav__logo" }
const _hoisted_5 = {
  key: 0,
  class: "c-gen-nav__search"
}
const _hoisted_6 = { class: "c-gen-nav__search-box" }
const _hoisted_7 = ["placeholder"]
const _hoisted_8 = {
  key: 0,
  class: "c-gen-nav__search-empty"
}
const _hoisted_9 = {
  key: 1,
  class: "c-gen-nav__search-normal"
}
const _hoisted_10 = {
  key: 1,
  class: "c-gen-nav__mobile-search-box"
}
const _hoisted_11 = { class: "c-gen-nav__mobile-search-input-wrap" }
const _hoisted_12 = ["placeholder"]
const _hoisted_13 = {
  key: 0,
  class: "c-gen-nav__search-empty"
}
const _hoisted_14 = {
  key: 1,
  class: "c-gen-nav__search-normal"
}
const _hoisted_15 = { class: "c-gen-nav__actions" }
const _hoisted_16 = ["aria-expanded"]
const _hoisted_17 = { class: "c-gen-nav__user-menu" }
const _hoisted_18 = ["aria-expanded"]
const _hoisted_19 = { class: "c-gen-nav__language-menu" }

import { computed, ref, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { HeaderProps } from '@/types/components/Header';
import HeaderDesktop from './HeaderDesktop.vue';
import HeaderMobile from './HeaderMobile.vue';
import SiteImage from '@/elements/SiteImage.vue';
import SiteLink from '@/elements/SiteLink.vue';
import { useI18n } from 'vue-i18n';
import useScrollY from '@/hooks/useScrollY';
import utils from '@/utils';
import { appendQuery, getBetterUrl } from '@/utils/uri-utils';
import * as querystring from 'querystring';
import api from '@/api/index';
import { readMyAccountAuth } from '@/utils/myaccount-auth-reader';
import { webStorage } from '@/utils/web-storage';
import { S_LOGIN_INFO, S_USER_INFO } from '@/utils/web-storage-keys';
import { clearCookies } from '@/utils/cookies-util';

type LanguageOption = {
  code: string;
  label: string;
  href: string;
  link: any;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'Header',
  props: {
    page: {},
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const { scrollTop } = useScrollY();
const props = __props;
const route = useRoute();
const hasLogoSvg = computed(() => Boolean(props.fields?.logo?.value?.svgCode));

const defaultTheme = computed(
  () => utils.sp.getVal(props.page?.fields?.headerTheme) ?? 'transparent'
);

const theme = computed(() => {
  return scrollTop.value > 60 ? 'white' : defaultTheme.value;
});

const { t } = useI18n();
const menus = computed(() => props.fields?.menus ?? []);

const languageRef = ref<HTMLElement | null>(null);
const languageOpen = ref(false);
const userRef = ref<HTMLElement | null>(null);
const userMenuOpen = ref(false);
const authInfo = ref<any>({ isLoggedIn: false, source: null });
const languageOptions = computed<LanguageOption[]>(() => {
  return (props.fields?._languages ?? [])
    .map((item: any) => {
      const fields = item?.fields || item || {};
      const link = fields.link;
      const href = getLinkHref(link);

      return {
        code: utils.sc.getFieldValue(item, 'code') || '',
        label: utils.sc.getFieldValue(item, 'label') || '',
        href,
        link,
      };
    })
    .filter((item) => item.label && item.href);
});

const getPathname = (url: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).pathname;
    } catch (error) {
      return url;
    }
  }

  return url.split(/[?#]/)[0];
};

const normalizePathname = (url: string) => {
  const pathname = getPathname(url) || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '').toLowerCase() : pathname;
};

const getCurrentLanguage = () => {
  if (typeof route.params.lang === 'string') return route.params.lang;

  if (typeof window !== 'undefined') {
    const match = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    if (match?.[1]) return match[1];
  }

  return props.page?.itemLanguage || props.page?.language || 'en';
};

const normalizeLanguage = (language?: string) => language?.toLowerCase() || '';

const getLanguageBase = (language?: string) => normalizeLanguage(language).split('-')[0] || '';

const currentLanguageOption = computed(() => {
  const currentPathname =
    route.path || (typeof window !== 'undefined' ? window.location.pathname : '');
  const matchedByPath = languageOptions.value.find(
    (item) => normalizePathname(item.href) === normalizePathname(currentPathname)
  );

  if (matchedByPath) return matchedByPath;

  const currentLanguage = getCurrentLanguage();
  const currentLanguageKey = normalizeLanguage(currentLanguage);
  const currentLanguageBase = getLanguageBase(currentLanguage);

  return (
    languageOptions.value.find(
      (item) =>
        normalizeLanguage(item.code) === currentLanguageKey ||
        getLanguageBase(item.code) === currentLanguageBase
    ) || null
  );
});

const currentLanguageLabel = computed(() => {
  return (
    currentLanguageOption.value?.label ||
    getLanguageBase(getCurrentLanguage()).toUpperCase() ||
    'EN'
  );
});

const isCurrentLanguage = (language: string) => {
  if (currentLanguageOption.value?.code) {
    return normalizeLanguage(currentLanguageOption.value.code) === normalizeLanguage(language);
  }

  const currentLanguage = getCurrentLanguage();
  return (
    normalizeLanguage(currentLanguage) === normalizeLanguage(language) ||
    getLanguageBase(currentLanguage) === getLanguageBase(language)
  );
};

const toggleLanguage = () => {
  languageOpen.value = !languageOpen.value;
};

const closeLanguageOnOutsideClick = (event: MouseEvent) => {
  if (!languageRef.value?.contains(event.target as Node)) {
    languageOpen.value = false;
  }
};

const refreshAuthInfo = () => {
  authInfo.value = readMyAccountAuth();
};

const isLoggedIn = computed(() => Boolean(authInfo.value?.isLoggedIn));

const userDisplayName = computed(() => {
  const profile = authInfo.value?.profile || {};
  const user = authInfo.value?.user || {};
  return (
    profile.first_name || user.first_name || user.nickname || authInfo.value?.email || 'My Account'
  );
});

const myAccountUrl = computed(() => {
  return '/myportal/myporta';
});

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value;
};

const closeUserMenuOnOutsideClick = (event: MouseEvent) => {
  if (!userRef.value?.contains(event.target as Node)) {
    userMenuOpen.value = false;
  }
};

const goMyAccount = () => {
  userMenuOpen.value = false;
  if (typeof window !== 'undefined') {
    window.location.href = myAccountUrl.value;
  }
};

const handleSignOut = () => {
  webStorage.clear(S_USER_INFO);
  webStorage.clear(S_LOGIN_INFO);

  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('S_USER_INFO');
    window.localStorage.removeItem('S_LOGIN_INFO');
  }

  clearCookies('S_USER_SESSION');
  clearCookies('7e2411adf07bfb27e6326b47734bead7');
  authInfo.value = { isLoggedIn: false, source: null };
  userMenuOpen.value = false;

  const loginUrl = getLinkHref(props.fields?.signLink) || '/sign';
  if (typeof window !== 'undefined') {
    window.location.href = loginUrl;
  }
};

onMounted(() => {
  refreshAuthInfo();
  document.addEventListener('click', closeLanguageOnOutsideClick);
  document.addEventListener('click', closeUserMenuOnOutsideClick);
  window.addEventListener('storage', refreshAuthInfo);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeLanguageOnOutsideClick);
  document.removeEventListener('click', closeUserMenuOnOutsideClick);
  window.removeEventListener('storage', refreshAuthInfo);
});

const getLinkHref = (link: any) => {
  let url = link?.value?.href || link?.href || '';

  if (!url) return '';
  if (/^(mailto|tel):/gi.test(url)) return url;
  if (/^https?:\/\/((tel|mailto):|#)/gi.test(url)) return url.replace(/^https?:\/\//gi, '');

  const queryString = link?.value?.querystring || link?.querystring;
  if (queryString) {
    try {
      url = appendQuery(url, querystring.parse(queryString));
    } catch (error) {
      console.warn('Failed to parse querystring:', queryString, error);
    }
  }

  const anchor = link?.value?.anchor || link?.anchor;
  if (anchor) {
    url = `${url}?id=${anchor}`;
  }

  return getBetterUrl(url) ?? '';
};

const getLinkText = (link: any) => {
  return link?.value?.text || link?.text || getLinkHref(link);
};

const getLinkAttrs = (link: any) => {
  const value = link?.value || link || {};
  const attrs: Record<string, string> = {};

  if (value.target) attrs.target = value.target;
  if (value.title) attrs.title = value.title;
  if (value.class) attrs.class = value.class;
  if (value.target === '_blank') attrs.rel = 'noopener noreferrer';

  return attrs;
};

const createSiteLink = (href: string) => ({
  value: {
    href: href || '',
  },
});

// Mobile Menu State
const mobileMenuOpen = ref(false);
const showSearchBox = ref(false); // Control desktop search box visibility
const showMobileSearch = ref(false); // Control mobile search box visibility
const mobileSearchInput = ref(null);

// Search State
const searchQuery = ref('');
const showSearchResults = ref(false);
const searchResults = ref<any[]>([]);
const isSearching = ref(false);
let hideTimer: number | null = null;
let searchTimer: number | null = null;
let searchRequestId = 0;
const isMouseOverResults = ref(false);

// API search results
const filteredResults = computed(() => {
  return searchResults.value;
});

// Category results
const categoryResults = computed(() => {
  return [];
});

// Normal results
const normalResults = computed(() => {
  return searchResults.value;
});

// Search handling - watch searchQuery with 500ms debounce
watch(searchQuery, (newVal) => {
  const keyword = newVal.trim();

  if (!keyword) {
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    searchResults.value = [];
    isSearching.value = false;
    return;
  }

  if (searchTimer) {
    clearTimeout(searchTimer);
  }

  searchTimer = window.setTimeout(async () => {
    searchTimer = null;
    const currentRequestId = ++searchRequestId;
    isSearching.value = true;

    try {
      const [res, error] = await api.search.getSearch({
        q: keyword,
        page: 1,
        limit: 1000,
      });

      // Discard stale response if a newer request was fired
      if (currentRequestId !== searchRequestId) {
        return;
      }

      if (res) {
        const contentList = res.content?.list || [];
        const webinarList = res.webinar?.list || [];
        searchResults.value = [...contentList, ...webinarList];
      } else {
        searchResults.value = [];
      }
    } catch (e) {
      if (currentRequestId !== searchRequestId) {
        return;
      }
      searchResults.value = [];
    } finally {
      if (currentRequestId === searchRequestId) {
        isSearching.value = false;
        showSearchResults.value = true;
      }
    }
  }, 500);
});

const handleBlur = () => {
  // If the mouse is over the results dropdown (e.g., user is scrolling/hovering),
  // do not hide the dropdown even if the input loses focus.
  if (isMouseOverResults.value) {
    return;
  }
  hideTimer = window.setTimeout(() => {
    showSearchResults.value = false;
    searchQuery.value = '';
    showSearchBox.value = false;
    showMobileSearch.value = false;
  }, 200);
};

const handleSearchResultsMouseEnter = () => {
  isMouseOverResults.value = true;
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
};

const handleSearchResultsMouseLeave = () => {
  isMouseOverResults.value = false;
  hideTimer = window.setTimeout(() => {
    showSearchResults.value = false;
    searchQuery.value = '';
    showSearchBox.value = false;
    showMobileSearch.value = false;
  }, 200);
};

const handleSearchIconClick = () => {
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
  showSearchResults.value = false;

  const keyword = searchQuery.value;
  searchQuery.value = '';
  showSearchBox.value = false;
  showMobileSearch.value = false;

  const searchLink = props?.fields?.searchLink ? getLinkHref(props.fields.searchLink) : '';
  if (searchLink) {
    const url = new URL(searchLink, window.location.origin);
    if (keyword) {
      url.searchParams.set('q', keyword);
    }
    window.location.href = url.toString();
  }
};

const handleSearchResultClick = (url: string) => {
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
  showSearchResults.value = false;
  searchQuery.value = '';
  showSearchBox.value = false;
  showMobileSearch.value = false;

  if (url) {
    window.location.href = url;
  }
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const toggleSearch = () => {
  showSearchBox.value = !showSearchBox.value;
  if (!showSearchBox.value) {
    searchQuery.value = '';
    showSearchResults.value = false;
  }
};

const openMobileSearch = () => {
  showMobileSearch.value = true;
  nextTick(() => {
    if (mobileSearchInput.value) {
      (mobileSearchInput.value as HTMLInputElement).focus();
    }
  });
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_JssRichText = _resolveComponent("JssRichText")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (defaultTheme.value !== 'transparent')
      ? (_openBlock(), _createElementBlock("div", _hoisted_1))
      : _createCommentVNode("", true),
    (__props.fields)
      ? (_openBlock(), _createElementBlock("header", {
          key: 1,
          class: _normalizeClass(["c-gen-nav", theme.value])
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(SiteLink, {
                  link: createSiteLink('/')
                }, {
                  default: _withCtx(() => [
                    (hasLogoSvg.value)
                      ? (_openBlock(), _createBlock(_component_Icon, {
                          key: 0,
                          field: __props.fields.logo,
                          class: "c-gen-nav__logo"
                        }, null, 8, ["field"]))
                      : (__props.fields.logo)
                        ? (_openBlock(), _createBlock(SiteImage, {
                            key: 1,
                            field: __props.fields.logo,
                            loading: "eager",
                            class: "c-gen-nav__logo"
                          }, null, 8, ["field"]))
                        : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["link"])
              ]),
              _createVNode(HeaderDesktop, _normalizeProps(_guardReactiveProps(props)), null, 16),
              (showSearchBox.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _withDirectives(_createElementVNode("input", {
                        type: "text",
                        placeholder: __props.fields.searchPlaceholder.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                        onFocus: _cache[1] || (_cache[1] = ($event: any) => (showSearchResults.value = true)),
                        onBlur: handleBlur,
                        onKeyup: _withKeys(handleSearchIconClick, ["enter"])
                      }, null, 40, _hoisted_7), [
                        [_vModelText, searchQuery.value]
                      ]),
                      _createElementVNode("button", {
                        class: "c-gen-nav__search-btn",
                        onClick: _withModifiers(handleSearchIconClick, ["stop"])
                      }, [
                        _createVNode(_component_Icon, {
                          name: "search",
                          class: "c-gen-nav__icon c-gen-nav__search-icon"
                        })
                      ])
                    ]),
                    _withDirectives(_createElementVNode("div", {
                      class: "c-gen-nav__search-results",
                      onMouseenter: handleSearchResultsMouseEnter,
                      onMouseleave: handleSearchResultsMouseLeave
                    }, [
                      (filteredResults.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(__props.fields.searchEmpty.value), 1))
                        : _createCommentVNode("", true),
                      (filteredResults.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(normalResults.value, (item, index) => {
                              return (_openBlock(), _createBlock(SiteLink, {
                                class: "c-gen-nav__search-item",
                                key: 'norm-' + index,
                                link: createSiteLink((item as any).url),
                                onClick: _withModifiers(($event: any) => (handleSearchResultClick((item as any).url)), ["stop"])
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_JssRichText, {
                                    field: { value: (item as any).title }
                                  }, null, 8, ["field"])
                                ]),
                                _: 2
                              }, 1032, ["link", "onClick"]))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ], 544), [
                      [_vShow, showSearchResults.value && searchQuery.value.trim()]
                    ])
                  ]))
                : _createCommentVNode("", true),
              (showMobileSearch.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, [
                      _withDirectives(_createElementVNode("input", {
                        type: "text",
                        placeholder: __props.fields.searchPlaceholder.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchQuery).value = $event)),
                        onFocus: _cache[3] || (_cache[3] = ($event: any) => (showSearchResults.value = true)),
                        onBlur: handleBlur,
                        onKeyup: _withKeys(handleSearchIconClick, ["enter"]),
                        ref_key: "mobileSearchInput",
                        ref: mobileSearchInput
                      }, null, 40, _hoisted_12), [
                        [_vModelText, searchQuery.value]
                      ]),
                      _createElementVNode("button", {
                        class: "c-gen-nav__search-btn",
                        onClick: _withModifiers(handleSearchIconClick, ["stop"])
                      }, [
                        _createVNode(_component_Icon, {
                          name: "search",
                          class: "c-gen-nav__icon c-gen-nav__search-icon"
                        })
                      ])
                    ]),
                    _withDirectives(_createElementVNode("div", {
                      class: "c-gen-nav__search-results",
                      onMouseenter: handleSearchResultsMouseEnter,
                      onMouseleave: handleSearchResultsMouseLeave
                    }, [
                      (filteredResults.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(__props.fields.searchEmpty.value), 1))
                        : _createCommentVNode("", true),
                      (filteredResults.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(normalResults.value, (item, index) => {
                              return (_openBlock(), _createBlock(SiteLink, {
                                class: "c-gen-nav__search-item",
                                key: 'norm-' + index,
                                link: createSiteLink((item as any).url),
                                onClick: _withModifiers(($event: any) => (handleSearchResultClick((item as any).url)), ["stop"])
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_JssRichText, {
                                    field: { value: (item as any).title }
                                  }, null, 8, ["field"])
                                ]),
                                _: 2
                              }, 1032, ["link", "onClick"]))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ], 544), [
                      [_vShow, showSearchResults.value && searchQuery.value.trim()]
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("button", {
                  class: "c-gen-nav__mobile-search",
                  onClick: openMobileSearch
                }, [
                  _createVNode(_component_Icon, {
                    name: "search",
                    class: "c-gen-nav__icon"
                  })
                ]),
                (!showSearchBox.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "c-gen-nav__desktop-search",
                      onClick: toggleSearch
                    }, [
                      _createVNode(_component_Icon, {
                        name: "search",
                        class: "c-gen-nav__icon"
                      })
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: "c-gen-nav__user",
                  ref_key: "userRef",
                  ref: userRef
                }, [
                  (isLoggedIn.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createElementVNode("button", {
                          class: "c-gen-nav__user-toggle",
                          type: "button",
                          "aria-expanded": userMenuOpen.value,
                          onClick: _withModifiers(toggleUserMenu, ["stop"])
                        }, [
                          _createVNode(_component_Icon, {
                            name: "user",
                            class: "c-gen-nav__icon"
                          }),
                          _createElementVNode("span", null, _toDisplayString(userDisplayName.value), 1)
                        ], 8, _hoisted_16),
                        _withDirectives(_createElementVNode("div", _hoisted_17, [
                          _createElementVNode("button", {
                            class: "c-gen-nav__user-menu-item c-gen-nav__user-menu-item--primary",
                            type: "button",
                            onClick: goMyAccount
                          }, " My Account "),
                          _createElementVNode("button", {
                            class: "c-gen-nav__user-menu-item",
                            type: "button",
                            onClick: handleSignOut
                          }, " Sign Out ")
                        ], 512), [
                          [_vShow, userMenuOpen.value]
                        ])
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(_component_Icon, {
                          name: "user",
                          class: "c-gen-nav__icon"
                        }),
                        (getLinkHref(__props.fields.signLink))
                          ? (_openBlock(), _createBlock(SiteLink, _mergeProps({
                              key: 0,
                              link: __props.fields.signLink
                            }, getLinkAttrs(__props.fields.signLink)), {
                              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                _createTextVNode(" Sign in ", -1)
                              ]))]),
                              _: 1
                            }, 16, ["link"]))
                          : _createCommentVNode("", true),
                        _cache[8] || (_cache[8] = _createTextVNode(" | ", -1)),
                        (getLinkHref(__props.fields.registerLink))
                          ? (_openBlock(), _createBlock(SiteLink, _mergeProps({
                              key: 1,
                              link: __props.fields.registerLink
                            }, getLinkAttrs(__props.fields.registerLink)), {
                              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                _createTextVNode(" Register ", -1)
                              ]))]),
                              _: 1
                            }, 16, ["link"]))
                          : _createCommentVNode("", true)
                      ], 64))
                ], 512),
                _createElementVNode("div", {
                  class: "c-gen-nav__language",
                  ref_key: "languageRef",
                  ref: languageRef
                }, [
                  _createElementVNode("button", {
                    class: "c-gen-nav__language-toggle",
                    type: "button",
                    "aria-expanded": languageOpen.value,
                    "aria-label": "Switch language",
                    onClick: toggleLanguage
                  }, [
                    _cache[9] || (_cache[9] = _createElementVNode("span", {
                      class: "c-gen-nav__language-globe",
                      "aria-hidden": "true"
                    }, null, -1)),
                    _createElementVNode("span", null, _toDisplayString(currentLanguageLabel.value), 1),
                    _createVNode(_component_Icon, {
                      name: "caretDown",
                      class: "c-gen-nav__language-arrow"
                    })
                  ], 8, _hoisted_18),
                  _withDirectives(_createElementVNode("div", _hoisted_19, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(languageOptions.value, (item) => {
                      return (_openBlock(), _createBlock(SiteLink, {
                        key: item.code || item.label,
                        class: _normalizeClass(["c-gen-nav__language-option", { 'is-active': isCurrentLanguage(item.code) }]),
                        link: item.link,
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (languageOpen.value = false))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["class", "link"]))
                    }), 128))
                  ], 512), [
                    [_vShow, languageOpen.value]
                  ])
                ], 512),
                _createElementVNode("button", {
                  class: "c-gen-nav__mobile-toggle",
                  onClick: toggleMobileMenu
                }, [
                  _createVNode(_component_Icon, {
                    name: "menu",
                    class: "c-gen-nav__icon c-gen-nav__icon--large"
                  })
                ])
              ])
            ])
          ]),
          _createVNode(HeaderMobile, {
            menus: menus.value,
            isOpen: mobileMenuOpen.value,
            onClose: _cache[5] || (_cache[5] = ($event: any) => (mobileMenuOpen.value = false))
          }, null, 8, ["menus", "isOpen"])
        ], 2))
      : _createCommentVNode("", true)
  ], 64))
}
}

})